function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { FC, ReactNode, Ref, CSSProperties, useState, useCallback, useRef, MouseEventHandler, MouseEvent } from 'react';
import cn from 'classnames';
import { RenderOverride, useRenderOverride } from '../../lib/render-override';
import { useUniqId } from '../../hooks/useUniqId';
import { useUpdateEffect } from '../../hooks/useUpdateEffect';
import { mergeAllRefs } from '../../lib/mergeRefs';
import { IWithControlProps, withControl } from '../withControl/withControl';
import { IWithControlProps as IWithControlDesktopProps, withControl as withControlDesktop } from '../withControl/withControl@desktop';
import { withDebounceInput } from '../withDebounceInput';
import { withAutoFocus } from '../withAutoFocus/withAutoFocus';
import { Icon } from '../Icon/Icon';
import { ITextInputControlProps, TextInputControl as ControlOriginal } from './Control/TextInput-Control';
import { TextInputBox as Box } from './Box/TextInput-Box';
import { TextInputHint as Hint } from './Hint/TextInput-Hint';
import { TextInputLabel as Label } from './Label/TextInput-Label';
import { TextInputCounter as Counter } from './Counter/TextInput-Counter';
import { TextInputClear as Clear } from './Clear/TextInput-Clear';
import { IconNameMedium, IconNameSmall } from '../Icon/icons';
require("./TextInput.css");
var styles = {
  "Root": "TextInput-module-Root_1xh",
  "ControlWrapper": "TextInput-module-ControlWrapper_3Qg",
  "Top": "TextInput-module-Top_2gG",
  "endContent": "TextInput-module-endContent_2Q_",
  "AddonsGroup": "TextInput-module-AddonsGroup_7Qy",
  "AddonAfter": "TextInput-module-AddonAfter_4Xm",
  "AddonBefore": "TextInput-module-AddonBefore_1dv",
  "dependentLabel": "TextInput-module-dependentLabel_1cw",
  "focused": "TextInput-module-focused_2qR",
  "filled": "TextInput-module-filled_1hr",
  "Label": "TextInput-module-Label_3yd",
  "sSize": "TextInput-module-sSize_2OV",
  "Box": "TextInput-module-Box_yYv",
  "Counter": "TextInput-module-Counter_3Nt",
  "Control": "TextInput-module-Control_8w_",
  "Hint": "TextInput-module-Hint_2q4",
  "Clear": "TextInput-module-Clear_3fq",
  "Icon": "TextInput-module-Icon_2bI",
  "mSize": "TextInput-module-mSize_K0y",
  "lSize": "TextInput-module-lSize_2wC",
  "defaultVariant": "TextInput-module-defaultVariant_2Xe",
  "TextInput-Box": "TextInput-module-TextInput-Box_2Wl",
  "errorState": "TextInput-module-errorState_1jA",
  "hovered": "TextInput-module-hovered_3mP",
  "disabled": "TextInput-module-disabled_2wU"
};
export const TextInputSizes = {
  s: 's',
  m: 'm',
  l: 'l'
};
export const TextInputPins = {
  brickRound: 'brickRound',
  clearClear: 'clearClear',
  clearRound: 'clearRound',
  roundBrick: 'roundBrick',
  roundClear: 'roundClear',
  roundRound: 'roundRound'
};
export const TextInputVariants = {
  default: 'default'
};
/**
 * Однострочное текстовое поле.
 * @param {ITextInputProps} props
 */
const TextInputBase = ({
  addonAfter,
  addonBefore,
  className,
  classNameBox,
  disabled,
  focused,
  hovered,
  iconLeft,
  iconRight,
  innerRef,
  controlRef,
  onChange,
  onMouseEnter,
  onMouseLeave,
  style,
  // @ts-ignore
  pressed: _pressed,
  pin,
  size = TextInputSizes.l,
  nativeSize,
  variant = TextInputVariants.default,
  hasClear = false,
  hint: htmlHint,
  label,
  placeholder,
  hasIndependentLabel = true,
  state,
  title,
  renderControl,
  required = false,
  maxCharactersLength,
  showCharactersCounter,
  ...props
}) => {
  const Control = useRenderOverride(ControlOriginal, renderControl);
  const showLabel = label && hasIndependentLabel;
  const showCounter = Boolean(maxCharactersLength) && showCharactersCounter && props.type === 'text';
  const inputRef = useRef(null);
  const [hint, setHint] = useState(htmlHint);
  const [hintLeave, setHintLeave] = useState(false);
  const prevHint = useRef(htmlHint);
  const hintId = useUniqId('hint');
  const inputId = useUniqId('input');
  const isDependentLabel = Boolean(label && !hasIndependentLabel);
  useUpdateEffect(() => {
    if (htmlHint) {
      setHint(htmlHint);
    } else if (prevHint.current) {
      setHintLeave(true);
    }
    prevHint.current = htmlHint;
  }, [htmlHint]);
  const onAnimationEnd = useCallback(() => {
    if (!htmlHint) {
      setHint('');
      setHintLeave(false);
    }
  }, [htmlHint]);
  const onClear = useCallback(event => {
    event.preventDefault();
    if (!inputRef.current) return;
    inputRef.current.focus();
    if (onChange !== undefined) {
      const originalValue = inputRef.current.value;

      // Создаем синтетическое событие для эмуляции очистки контрола.
      const syntheticEvent = Object.create(event);
      syntheticEvent.target = inputRef.current;
      syntheticEvent.currentTarget = inputRef.current;
      inputRef.current.value = '';
      onChange(syntheticEvent);
      // Восстанавливаем предыдущее значение на тот случай,
      // если в обработчике onChange не было установлено пустое значение.
      inputRef.current.value = originalValue;
      inputRef.current.focus();
    }
  }, [onChange]);
  const labelNode = /*#__PURE__*/React.createElement(Label, {
    required: required,
    className: styles.Label,
    text: label || '',
    inputId: inputId,
    dependent: isDependentLabel,
    size: size
  });
  return /*#__PURE__*/React.createElement("span", {
    className: cn(styles.Root, styles[`${size}Size`], styles[`${variant}Variant`], styles[`${state}State`], {
      [styles.disabled]: disabled,
      [styles.hovered]: hovered,
      [styles.focused]: focused,
      [styles.dependentLabel]: isDependentLabel,
      [styles.filled]: Boolean(props.value)
    }, className),
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    ref: innerRef,
    style: style,
    title: title
  }, (showLabel || showCounter) && /*#__PURE__*/React.createElement("div", {
    className: cn(styles.Top, styles[`${size}Size`], {
      [styles.endContent]: !showLabel && showCounter
    })
  }, showLabel && labelNode, showCounter && /*#__PURE__*/React.createElement(Counter, {
    current: String(props.value)?.length,
    max: maxCharactersLength,
    className: styles.Counter
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.AddonsGroup
  }, addonBefore && /*#__PURE__*/React.createElement("div", {
    className: styles.AddonBefore
  }, addonBefore), iconLeft && /*#__PURE__*/React.createElement(Icon, {
    className: styles.Icon,
    name: iconLeft
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.ControlWrapper
  }, label && !hasIndependentLabel && labelNode, /*#__PURE__*/React.createElement(Control, _extends({}, props, {
    onChange: onChange,
    controlRef: mergeAllRefs(controlRef, inputRef),
    id: inputId,
    required: required,
    maxLength: maxCharactersLength,
    placeholder: hasIndependentLabel ? placeholder : '',
    "aria-invalid": state === 'error',
    disabled: disabled,
    className: styles.Control,
    "aria-describedby": hint ? hintId : undefined,
    size: nativeSize
  }))), hasClear && /*#__PURE__*/React.createElement(Clear, {
    className: styles.Clear,
    visible: Boolean(props.value),
    disabled: disabled,
    onPointerDown: onClear
  }), iconRight && /*#__PURE__*/React.createElement(Icon, {
    className: styles.Icon,
    name: iconRight
  }), addonAfter && /*#__PURE__*/React.createElement("div", {
    className: styles.AddonAfter
  }, addonAfter), /*#__PURE__*/React.createElement(Box, {
    pin: pin,
    className: cn(styles.Box, classNameBox)
  })), hint && /*#__PURE__*/React.createElement(Hint, {
    leave: hintLeave,
    onAnimationEnd: onAnimationEnd,
    id: hintId,
    className: styles.Hint
  }, hint));
};

/**
 * @deprecated вместо этого используйте {@link FieldText}
 */
export const TextInput = withDebounceInput(withAutoFocus(withControl(withControlDesktop(TextInputBase))));
if (process.env.NODE_ENV !== 'production') {
  TextInput.displayName = 'TextInput';
}